// 作者: Yiyu Zhang(zhangyiyu@ihep.ac.cn)
// 全局用户信息存储：React Context + localStorage 同步。
// 任何组件可通过 useUser() 读取/更新用户信息；getUserMine 等接口写入
// localStorage 后会派发 default-user-info-changed 事件，本 Context 自动同步。
import React, { createContext, useContext, useState, useEffect, useCallback } from "react"

export interface UserInfo {
  id?: string
  username?: string | null
  email?: string | null
  user_level?: string | null
  user_groups?: Array<{
    name?: string
    permission?: { pages?: string[] } | null
  }> | null
  balance?: number
  tip?: string | null
  [key: string]: unknown
}

interface UserContextValue {
  user: UserInfo | null
  setUser: (user: UserInfo | null) => void
  refreshUser: () => void
  clearUser: () => void
  isAdmin: boolean
  pagePermissions: string[]
}

const UserContext = createContext<UserContextValue | undefined>(undefined)

const USER_INFO_KEY = "user_info"

function readUserFromStorage(): UserInfo | null {
  if (typeof window === "undefined") return null
  const raw = localStorage.getItem(USER_INFO_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as UserInfo
  } catch {
    return null
  }
}

function computePagePermissions(user: UserInfo | null): string[] {
  if (!user) return []
  const visiblePages = new Set<string>()
  for (const g of user.user_groups || []) {
    const pages = g?.permission?.pages || []
    pages.forEach((p: string) => visiblePages.add(p))
  }
  if (user.user_level === "admin") {
    visiblePages.add("usage_stats")
    visiblePages.add("usage_analysis")
  }
  return Array.from(visiblePages)
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUserState] = useState<UserInfo | null>(() =>
    readUserFromStorage()
  )

  const syncFromStorage = useCallback(() => {
    setUserState(readUserFromStorage())
  }, [])

  // 首次挂载同步一次
  useEffect(() => {
    syncFromStorage()
  }, [syncFromStorage])

  // 监听 default-user-info-changed（getUserMine 写入后派发）和
  // storage 事件（跨 tab 同步）
  useEffect(() => {
    if (typeof window === "undefined") return
    window.addEventListener("default-user-info-changed", syncFromStorage)
    window.addEventListener("default-api-key-changed", syncFromStorage)
    window.addEventListener("storage", syncFromStorage)
    return () => {
      window.removeEventListener("default-user-info-changed", syncFromStorage)
      window.removeEventListener("default-api-key-changed", syncFromStorage)
      window.removeEventListener("storage", syncFromStorage)
    }
  }, [syncFromStorage])

  const setUser = useCallback((next: UserInfo | null) => {
    setUserState(next)
    if (typeof window !== "undefined") {
      if (next) {
        localStorage.setItem(USER_INFO_KEY, JSON.stringify(next))
      } else {
        localStorage.removeItem(USER_INFO_KEY)
      }
      window.dispatchEvent(new Event("default-user-info-changed"))
    }
  }, [])

  const refreshUser = useCallback(() => {
    syncFromStorage()
  }, [syncFromStorage])

  const clearUser = useCallback(() => {
    setUserState(null)
    if (typeof window !== "undefined") {
      localStorage.removeItem(USER_INFO_KEY)
      window.dispatchEvent(new Event("default-user-info-changed"))
    }
  }, [])

  const isAdmin = user?.user_level === "admin"
  const pagePermissions = computePagePermissions(user)

  return (
    <UserContext.Provider
      value={{ user, setUser, refreshUser, clearUser, isAdmin, pagePermissions }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext)
  if (!ctx) {
    throw new Error("useUser 必须在 UserProvider 内部使用")
  }
  return ctx
}

export default UserContext
